import { useCallback, useEffect, useRef, useState } from 'react';
import {
  explainSpeechError,
  getSpeechRecognitionCtor,
  isSpeechRecognitionSupported,
  type SpeechStatus,
  type SpeechRecognitionInstance,
  type UseSpeechToText,
} from '@ai-tiaozs/shared';

/** 同 web-a 实现,放在 web-b 避免 cross-app 依赖 */
export function useSpeechToText(options: { lang?: string; interim?: boolean } = {}): UseSpeechToText {
  const lang = options.lang ?? 'zh-CN';
  const interim = options.interim ?? true;
  const isSupported = isSpeechRecognitionSupported();

  const [status, setStatus] = useState<SpeechStatus>(isSupported ? 'idle' : 'unsupported');
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const recRef = useRef<SpeechRecognitionInstance | null>(null);
  const finalTextRef = useRef('');

  const cleanup = useCallback(() => {
    const r = recRef.current;
    if (r) {
      try { r.abort(); } catch { /* ignore */ }
      r.onresult = null;
      r.onerror = null;
      r.onend = null;
      recRef.current = null;
    }
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const start = useCallback(() => {
    if (!isSupported) {
      setError('当前浏览器不支持语音识别,请用 Chrome/Edge 或直接输入文字');
      setStatus('unsupported');
      return;
    }
    if (status === 'recording') return;
    setError(null);
    finalTextRef.current = '';
    setText('');
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) return;
    const r = new Ctor();
    r.lang = lang;
    r.continuous = false;
    r.interimResults = interim;
    r.maxAlternatives = 1;
    r.onresult = (ev) => {
      let interimText = '';
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const result = ev.results[i];
        const transcript = result[0]?.transcript ?? '';
        if (result.isFinal) {
          finalTextRef.current += transcript;
        } else {
          interimText += transcript;
        }
      }
      setText((finalTextRef.current + interimText).trim());
    };
    r.onerror = (ev) => {
      setError(explainSpeechError(ev.error));
      setStatus('idle');
      cleanup();
    };
    r.onend = () => {
      // 确保最终结果写入 text 后再切 idle,避免 VoiceInput 读到空文本
      setText((prev) => (finalTextRef.current || prev).trim());
      setStatus('idle');
    };
    recRef.current = r;
    try {
      r.start();
      setStatus('recording');
    } catch (e) {
      setError(e instanceof Error ? e.message : '启动录音失败');
      setStatus('idle');
      cleanup();
    }
  }, [isSupported, lang, interim, status, cleanup]);

  const stop = useCallback(() => {
    const r = recRef.current;
    if (r) {
      try {
        r.stop();
      } catch {
        // ignore
      }
    }
    // idle 由 onend 设置,避免在 onresult 之前就把 status 置 idle
  }, []);

  const reset = useCallback(() => {
    finalTextRef.current = '';
    setText('');
    setError(null);
  }, []);

  return { status, text, error, start, stop, reset, isSupported };
}
